import { Category } from "@redotech/redo-model/advanced-flow/triggers";
import { GenericDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/generic-types";
import { EmailBlockType, Section } from "@redotech/redo-model/email-builder";
import {
  buttonDefault,
  discountDefault,
  footerDefault,
  headerDefaultLogo,
  imageDefault,
  interactiveCartDefault,
  lineDefault,
  menuDefault,
  reviewRequestDefault,
  socialsDefault,
  spacerDefault,
  textDefault,
  trackableSummaryDefault,
  trackingInfoDefault,
} from "@redotech/redo-model/email-template-defaults";
import { NamedExoticComponent, ReactNode } from "react";
import ListIcon from "../arbiter-icon/dotpoints-01_filled.svg";
import HeadingIcon from "../arbiter-icon/heading-02.svg";
import ImageIcon from "../arbiter-icon/image-01.svg";
import LineIcon from "../arbiter-icon/minus.svg";
import MenuIcon from "../arbiter-icon/passcode.svg";
import PlusSquareIcon from "../arbiter-icon/plus-square.svg";
import ShoppingBagIcon from "../arbiter-icon/shopping-bag-02.svg";
import SpacerIcon from "../arbiter-icon/spacing-height-01_filled.svg";
import StarIcon from "../arbiter-icon/star-01.svg";
import DiscountIcon from "../arbiter-icon/tag-01.svg";
import SocialsIcon from "../arbiter-icon/thumbs-up.svg";
import TruckIcon from "../arbiter-icon/truck-01.svg";
import TypeIcon from "../arbiter-icon/type-01_filled.svg";
import FooterIcon from "../icon-old/footer.svg";
import { EmailButton } from "./blocks/button";
import { EmailDiscount } from "./blocks/discount";
import { EmailFooter } from "./blocks/footer";
import { EmailHeader } from "./blocks/header";
import { EmailImage } from "./blocks/image";
import { EmailInteractiveCart } from "./blocks/interactive-cart";
import { EmailLine } from "./blocks/line";
import { EmailMenu } from "./blocks/menu";
import { EmailOrderSummary } from "./blocks/order-summary";
import { EmailReviewRequest } from "./blocks/review-request";
import { EmailSocials } from "./blocks/socials";
import { EmailSpacer } from "./blocks/spacer";
import { EmailText } from "./blocks/text";
import { EmailTrackingInfo } from "./blocks/tracking-info";
export interface EmailBlock<T extends Section> {
  name: string;
  Component: NamedExoticComponent<any>;
  Icon: ReactNode;
  dataTypes?: GenericDataType[];
  alwaysAvailable?: boolean;
  categories?: Category[]; // this can probably be removed since the presence of a data type is enough to decide whether to show a block
  default: T;
}

const trackableSummary: EmailBlock<Section.TrackableSummary> = {
  name: "Order summary",
  Component: EmailOrderSummary,
  Icon: <ListIcon />,
  dataTypes: ["Trackable Summary"],
  categories: ["Order tracking", "Return tracking"],
  default: trackableSummaryDefault,
};

const trackingInfo: EmailBlock<Section.TrackingInfo> = {
  name: "Tracking info",
  Component: EmailTrackingInfo,
  Icon: <TruckIcon />,
  dataTypes: ["Tracker"],
  categories: ["Order tracking", "Return tracking"],
  default: trackingInfoDefault,
};

const button: EmailBlock<Section.Button> = {
  name: "Button",
  Component: EmailButton,
  Icon: <PlusSquareIcon />,
  dataTypes: ["Url", "Maybe Url"],
  alwaysAvailable: true,
  default: buttonDefault,
};

const interactiveCart: EmailBlock<Section.InteractiveCart> = {
  name: "Products",
  Component: EmailInteractiveCart,
  Icon: <ShoppingBagIcon />,
  dataTypes: ["Interactive Cart", "Maybe Interactive Cart"],
  default: interactiveCartDefault,
};

const reviewRequest: EmailBlock<Section.ReviewRequest> = {
  name: "Review request",
  Component: EmailReviewRequest,
  dataTypes: ["Review Request"],
  Icon: <StarIcon />,
  default: reviewRequestDefault,
};

const header: EmailBlock<Section.Header> = {
  name: "Header",
  Component: EmailHeader,
  Icon: <HeadingIcon />,
  default: headerDefaultLogo,
};

const footer: EmailBlock<Section.Footer> = {
  name: "Footer",
  Component: EmailFooter,
  Icon: <FooterIcon />,
  default: footerDefault,
};

const image: EmailBlock<Section.Image> = {
  name: "Image",
  Component: EmailImage,
  Icon: <ImageIcon />,
  default: imageDefault,
};

const text: EmailBlock<Section.Text> = {
  name: "Text",
  Component: EmailText,
  Icon: <TypeIcon />,
  default: textDefault,
};

const spacer: EmailBlock<Section.Spacer> = {
  name: "Spacer",
  Component: EmailSpacer,
  Icon: <SpacerIcon />,
  default: spacerDefault,
};

const line: EmailBlock<Section.Line> = {
  name: "Line",
  Component: EmailLine,
  Icon: <LineIcon />,
  default: lineDefault,
};

const menu: EmailBlock<Section.Menu> = {
  name: "Menu",
  Component: EmailMenu,
  Icon: <MenuIcon />,
  default: menuDefault,
};

const socials: EmailBlock<Section.Socials> = {
  name: "Socials",
  Component: EmailSocials,
  Icon: <SocialsIcon />,
  default: socialsDefault,
};

const discount: EmailBlock<Section.Discount> = {
  name: "Discount",
  Component: EmailDiscount,
  Icon: <DiscountIcon />,
  default: discountDefault,
};

export const emailBlocksExceptColumn: Record<
  Exclude<EmailBlockType, EmailBlockType.COLUMN>,
  EmailBlock<any>
> = {
  [EmailBlockType.HEADER]: header,
  [EmailBlockType.TEXT]: text,
  [EmailBlockType.BUTTON]: button,
  [EmailBlockType.LINE]: line,
  [EmailBlockType.SPACER]: spacer,
  [EmailBlockType.IMAGE]: image,
  [EmailBlockType.TRACKABLE_SUMMARY]: trackableSummary,
  [EmailBlockType.TRACKING_INFO]: trackingInfo,
  [EmailBlockType.INTERACTIVE_CART]: interactiveCart,
  [EmailBlockType.FOOTER]: footer,
  [EmailBlockType.REVIEW_REQUEST]: reviewRequest,
  [EmailBlockType.MENU]: menu,
  [EmailBlockType.SOCIALS]: socials,
  [EmailBlockType.DISCOUNT]: discount,
};
